import React, { useMemo, useEffect, useState, useRef } from "react";
import { Button } from "./button";
import { LoadingOverlay } from "./loader";
import { Upload, Plus } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { uuidV4 } from "../../utils/utils";
import {
  createDocumentResponseLocally,
  extensionList,
  assignDocIdAndPath,
  upload,
  validateFilesFormat,
  LoaderSection,
} from "./dragdropHelper";
import PropTypes from "prop-types";

const DragAndDrop = (props) => {
  const {
    loanId,
    isReadOnly,
    addMultipleDocumentsAPI = {},
    storeLocalOptions = {},
    remoteFetch,
    documentType,
    parentType,
    parentId = "",
    updateLoacalContextOnUpload = false,
    checkFilesFormat = true,
    dropZoneOptions = {},
    overRideFileFormat = null,
    maxFiles = null,
    postResponseOnUploadDoc = null,
    generatePreSignedUrl,
    uploadToAwsS3,
    toastError,
    allowedExtension = extensionList,
    supportedFormatsText = "PDF, DOC, XLS, JPG, PNG (Max 500MB)",
    showLoaderSection = true,
  } = props;

  const [prevAcceptedFiles, setPrevAcceptedFiles] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [progressInfos, setProgressInfos] = useState([]);
  const [startUpload, setStartUpload] = useState(false);
  const [isAllFilesUploaded, setIsAllFilesUploaded] = useState(false);
  const filesRef = useRef([]);

  useEffect(() => {
    if (errorMsg === "") return;
    toastError("Error", errorMsg, setErrorMsg(""));
  }, [errorMsg]);

  const {
    setLocalUploadedFiles = () => {},
    localUploadedFiles = [],
    localContext = { documents: [] },
    handleUpdateDocumentContext = () => {},
    contextKey = "documents",
  } = storeLocalOptions;

  const isUploadInProgress = useMemo(() => {
    return progressInfos.some(
      (info) => info.isUploaded === null && info.message === "Uploading..."
    );
  }, [progressInfos]);

  const onDrop = (acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      toastError("Error", fileRejections?.[0]?.errors?.[0]?.message, () => {});
    }
  };

  const { getRootProps, getInputProps, acceptedFiles, open } = useDropzone({
    noClick: true, // We'll handle click manually
    disabled: isReadOnly,
    noKeyboard: true,
    maxFiles,
    onDrop,
    ...dropZoneOptions,
  });

  const handleAddFilesClick = (e) => {
    e.stopPropagation();
    if (isUploadInProgress) {
      toastError(
        "Upload in Progress",
        "Please wait for the current upload to be completed.",
        () => {}
      );
      return;
    }
    if (!isReadOnly) {
      open();
    }
  };

  const storeFileDetailsLocally = () => {
    const fileValidations = validateFilesFormat(
      acceptedFiles,
      allowedExtension,
      setErrorMsg
    );
    if (!fileValidations) {
      const files = createDocumentResponseLocally(
        acceptedFiles,
        localContext,
        contextKey,
        documentType,
        parentType,
        parentId
      );
      const currentDocuments = [...localContext[contextKey], ...files];
      handleUpdateDocumentContext(
        contextKey,
        currentDocuments,
        "",
        localContext
      );
      const allAcceptedFilesToUpload = [...prevAcceptedFiles, ...acceptedFiles];
      const tempProgressInfos = acceptedFiles.map((file) => {
        const id = uuidV4();
        return {
          id,
          percentage: 0,
          fileName: file.name,
          message: "Uploading...",
          fileSize: file.size,
          description: "",
          path: null,
          isUploaded: null,
          errorType: null,
        };
      });
      setProgressInfos(tempProgressInfos);
      setLocalUploadedFiles(allAcceptedFilesToUpload);
      setPrevAcceptedFiles(allAcceptedFilesToUpload);
    }
  };

  useEffect(() => {
    if (acceptedFiles.length === 0) return;
    if (checkFilesFormat) {
      const fileValidations = validateFilesFormat(
        acceptedFiles,
        allowedExtension,
        setErrorMsg
      );
      if (fileValidations) return;
    }
    if (parentId !== "") {
      const allAcceptedFilesToUpload = [
        ...localUploadedFiles,
        ...acceptedFiles,
      ];
      setLocalUploadedFiles(allAcceptedFilesToUpload);
      const newFileEntries = acceptedFiles.map((file) => {
        const id = uuidV4();
        return { id, file };
      });
      filesRef.current = [...filesRef.current, ...newFileEntries];
      const tempProgressInfos = newFileEntries.map(({ id, file }) => {
        return {
          id,
          percentage: 0,
          fileName: file.name,
          message: "Uploading...",
          description: "",
          fileSize: file.size,
          path: null,
          isUploaded: null,
          errorType: null,
        };
      });
      setProgressInfos(tempProgressInfos);
      setStartUpload(true);
      setIsAllFilesUploaded(false);
      return;
    }
    storeFileDetailsLocally();
  }, [acceptedFiles]);

  useEffect(() => {
    if (!startUpload) return;
    filesRef.current.forEach(({ id, file }) => {
      upload({
        id,
        file,
        progressInfos,
        setProgressInfos,
        loanId,
        overRideFileFormat,
        generatePreSignedUrl: generatePreSignedUrl,
        uploadToAwsS3: uploadToAwsS3,
      });
    });

    setStartUpload(false);
  }, [startUpload]);

  const isReadyToProcessUpload = () => {
    if (isAllFilesUploaded) return false;
    if (!progressInfos?.length) return false;
    return !progressInfos.some((progressInfo) => {
      return progressInfo?.isUploaded === null;
    });
  };

  const filterUploadedDocs = (documents) => {
    return documents?.reduce((acc, doc) => {
      const progressInfo = progressInfos.find((p) => p.id === doc.id);
      if (progressInfo?.isUploaded) {
        acc.push({
          ...doc,
          path: progressInfo.path,
        });
      }
      return acc;
    }, []);
  };

  const updateProgressInfosWithApiData = (apiData) => {
    return progressInfos?.map((document) => {
      const filteredDoc = apiData.find((doc) => {
        return doc.path === document.path;
      });
      if (filteredDoc) {
        return {
          ...document,
          documentId: filteredDoc.documentId,
          createdOn: filteredDoc.createdOn,
        };
      }
      return document;
    });
  };

  const handleApiSuccess = (data) => {
    setIsAllFilesUploaded(true);
    if (data?.data) {
      setProgressInfos(updateProgressInfosWithApiData(data.data));
      
    }
    remoteFetch();
    if (!updateLoacalContextOnUpload) return;
    const files = assignDocIdAndPath(
      data?.data,
      localContext,
      contextKey,
      documentType,
      parentType,
      parentId
    );
    const currentDocuments = [...localContext[contextKey], ...files];
    handleUpdateDocumentContext(contextKey, currentDocuments, "", localContext);
  };

  const handleApiError = () => {
    toastError(
      "Error",
      "Error while associating uploaded document to parent.",
      () => {}
    );
  };

  useEffect(() => {
    if (!isReadyToProcessUpload()) return;
    // Extract just the file objects from filesRef for createDocumentResponseLocally
    const files = filesRef.current.map((entry) => entry.file);
    const documents = createDocumentResponseLocally(
      files,
      localContext,
      contextKey,
      documentType,
      parentType,
      parentId
    );
    // Add IDs to documents to match with progressInfos
    const documentsWithIds = documents.map((doc, index) => ({
      ...doc,
      id: filesRef.current[index].id,
    }));
    const updatedDocs = filterUploadedDocs(documentsWithIds);
    if (updatedDocs?.length > 0) {
      // Remove the id field before sending to API as it's only for internal tracking
      const docsForApi = updatedDocs.map(({ id, ...doc }) => doc);
      if (postResponseOnUploadDoc) {
        postResponseOnUploadDoc(docsForApi);
      } else {
        addMultipleDocumentsAPI.mutate(docsForApi, {
          onSuccess: handleApiSuccess,
          onError: handleApiError,
        });
      }
    }
  }, [progressInfos]);

  const isLoading = useMemo(() => {
    return addMultipleDocumentsAPI.isLoading;
  }, [addMultipleDocumentsAPI.isLoading]);
  const fileLabel = maxFiles === 1 ? "File" : "Files";

  const handleRetry = (id, skipSizeCheck = false) => {
    const fileEntry = filesRef.current.find((entry) => entry.id === id);
    if (!fileEntry) return;
    setIsAllFilesUploaded(false); // Reset so API can be called after retry succeeds
    setProgressInfos((prev) => {
      return prev.map((info) =>
        info.id === id
          ? {
              ...info,
              percentage: 0,
              message: "Uploading...",
              isUploaded: null,
              errorType: null,
            }
          : info
      );
    });
    upload({
      id,
      file: fileEntry.file,
      progressInfos,
      setProgressInfos,
      loanId,
      overRideFileFormat,
      generatePreSignedUrl,
      uploadToAwsS3,
      skipSizeCheck,
    });
  };

  const handleRemove = (id) => {
    filesRef.current = filesRef.current.filter((entry) => entry.id !== id);
    setProgressInfos((prev) => prev.filter((p) => p.id !== id));
    // setLocalUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      {isLoading && (
        <LoadingOverlay
          isVisible={isLoading}
          variant="spinner"
          size="lg"
          message="Loading..."
        />
      )}
      <div className="min-w-100 p-5 sm:p-6 border-2   border-dashed border-[var(--color-primary-3)] ] sm:border-gray-300 rounded-[1.5rem] sm:rounded-md sm:text-center cursor-pointer bg-[var(--color-neutral-1)]">
        <div {...getRootProps()} className="flex flex-col sm:gap-4 ">
          <div className="flex sm:flex-col sm:w-auto sm:rounded w-[60vw] items-center">
            <div className="flex flex-col items-center rounded">
              <div className="sm:w-[80px] sm:h-[80px] w-[64px] h-[64px] rounded-full flex items-center justify-center bg-[var(--background-page)]">
                <Upload className="sm:w-5 sm:h-5 w-6 h-6 text-[var(--color-primary-3)]" />
              </div>
            </div>
            <div className="flex flex-col sm:items-center gap-2 pl-[1.75rem] sm:pl-0 ">
              <p className="font-semibold text-base hidden sm:block sm:pt-[1rem]">
                Drag & drop files here{" "}
              </p>
              <p className="text-[0.9rem] sm:text-base sm:hidden block">
                Click the button <br />
                to browse your files
              </p>
              <p className="text-[0.63rem] sm:text-xs text-[var(--color-neutral-8)] sm:hidden">
                {supportedFormatsText}
              </p>
              <p className="text-xs text-gray-600 hidden sm:block">
                or click the button to browse your files
              </p>
            </div>
          </div>
          <div className="flex  sm:justify-center sm:p-0 pt-5">
            <Button
              className="flex w-full sm:w-auto "
              disabled={isReadOnly}
              variant="primary"
              onClick={handleAddFilesClick}
              type="button"
            >
              Add Files
              <Plus className="w-5 h-5" />
            </Button>
            <input type="file" {...getInputProps()} />
          </div>
          <div className="flex flex-col items-center">
            <p className="text-xs text-[var(--color-neutral-8)] hidden sm:block">
              {supportedFormatsText}
            </p>
          </div>
        </div>
      </div>
      {showLoaderSection && (
        <LoaderSection
          progressInfos={progressInfos}
          onRetry={handleRetry}
          onRemove={handleRemove}
          isUploadInProgress={isUploadInProgress}
        />
      )}
    </>
  );
};

DragAndDrop.propTypes = {
  loanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isReadOnly: PropTypes.bool,
  addMultipleDocumentsAPI: PropTypes.shape({
    mutate: PropTypes.func,
    isLoading: PropTypes.bool,
  }),
  storeLocalOptions: PropTypes.shape({
    setLocalUploadedFiles: PropTypes.func,
    localUploadedFiles: PropTypes.array,
    localContext: PropTypes.object,
    handleUpdateDocumentContext: PropTypes.func,
    contextKey: PropTypes.string,
  }),
  remoteFetch: PropTypes.func,
  documentType: PropTypes.string,
  parentType: PropTypes.string,
  parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  updateLoacalContextOnUpload: PropTypes.bool,
  checkFilesFormat: PropTypes.bool,
  dropZoneOptions: PropTypes.object,
  overRideFileFormat: PropTypes.any,
  maxFiles: PropTypes.number,
  postResponseOnUploadDoc: PropTypes.func,
  generatePreSignedUrl: PropTypes.func,
  uploadToAwsS3: PropTypes.func,
  toastError: PropTypes.func,
  allowedExtension: PropTypes.arrayOf(PropTypes.string),
  supportedFormatsText: PropTypes.string,
  showLoaderSection: PropTypes.bool,
};

export { DragAndDrop };
